// EMPLOYEE-PROFILE-1 (2026-07-08) — self-service employee profile on the
// Profile page: headshot, bio, specialties, licenses/certifications with
// document images + expiration tracking, and Promote-to-Website (admin
// approval flows through Admin → Website Publish). Entity-profile anatomy
// per LD-DESIGN-012 #2; typed metadata rows (#3); compact drop targets (#4).
import { useEffect, useRef, useState } from "react";
import { apiFetch, fmt } from "../api";
import { StatusPill, EmptyState, Spinner } from "./ui";

const CRED_TONE = { current: "green", expiring: "amber", expired: "red", no_expiry: "slate" };
const CRED_LABEL = { current: "Current", expiring: "Expiring", expired: "EXPIRED", no_expiry: "No expiry" };
const KIND_LABEL = { license: "License", certification: "Certification", other: "Other" };
const EMPTY_CRED = { kind: "license", name: "", jurisdiction: "", credential_no: "", issued_on: "", expires_on: "", notes: "" };

export default function EmployeeProfileSection({ user }) {
  const [data, setData] = useState(null);
  const [allSpecs, setAllSpecs] = useState([]);
  const [bio, setBio] = useState("");
  const [region, setRegion] = useState("");
  const [group, setGroup] = useState("Adjusters");
  const [summary, setSummary] = useState("");
  const [specIds, setSpecIds] = useState([]);
  const [credForm, setCredForm] = useState(null);   // null | {…} (id set = editing)
  const [headshotUrl, setHeadshotUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeVal, setProposeVal] = useState("");
  const fileRef = useRef(null);
  const credFileRef = useRef(null);
  const credFileTarget = useRef(null);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 6000); };

  const load = async () => {
    try {
      const [d, s] = await Promise.all([apiFetch("/me/profile"), apiFetch("/profile-specialties")]);
      setData(d); setAllSpecs(s.specialties || []);
      setBio(d.profile.bio || ""); setRegion(d.profile.region || "");
      setGroup(d.profile.website_group || "Adjusters");
      setSummary(d.profile.summary || "");
      setSpecIds((d.specialties || []).map(x => x.id));
      if (d.profile.headshot_updated_at) {
        try {
          const res = await apiFetch(`/users/${user.id}/headshot`, { raw: true });
          if (res.ok) setHeadshotUrl(URL.createObjectURL(await res.blob()));
        } catch { /* no headshot */ }
      } else setHeadshotUrl(null);
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user.id]);

  if (!data) return <div className="card"><div className="card-body"><Spinner label="Loading profile…" /></div></div>;

  const creds = data.credentials || [];
  const counts = creds.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});
  const publish = data.publish || {};
  const pending = publish.last_request && publish.last_request.status === "pending";
  const preview = data.website_preview || {};
  const initials = (user.full_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async () => {
    setBusy(true);
    try {
      await apiFetch("/me/profile", { method: "PATCH", body: { bio, region, summary, website_group: group } });
      await apiFetch("/me/profile/specialties", { method: "PUT", body: { specialty_ids: specIds } });
      flash("success", "Profile saved."); await load();
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const proposeSpecialty = async () => {
    const value = proposeVal.trim();
    if (!value) return;
    setBusy(true);
    try {
      const r = await apiFetch("/custom-lists/employee_specialties/propose", { method: "POST", body: { value } });
      const s = await apiFetch("/profile-specialties");
      setAllSpecs(s.specialties || []);
      if (r.item && !specIds.includes(r.item.id)) setSpecIds(prev => [...prev, r.item.id]);
      setProposeOpen(false); setProposeVal("");
      flash("success", (r.note || "Proposed — awaiting admin approval") + ". Click Save Profile to keep it on your profile.");
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const uploadHeadshot = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await apiFetch("/me/profile/headshot", { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      flash("success", "Headshot updated."); await load();
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const saveCred = async () => {
    setBusy(true);
    try {
      const body = { ...credForm };
      if (credForm.id) await apiFetch(`/me/credentials/${credForm.id}`, { method: "PATCH", body });
      else await apiFetch("/me/credentials", { method: "POST", body });
      setCredForm(null); flash("success", "Credential saved."); await load();
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const deleteCred = async (c) => {
    if (!window.confirm(`Remove "${c.name}"?`)) return;
    try { await apiFetch(`/me/credentials/${c.id}`, { method: "DELETE" }); await load(); }
    catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
  };

  const uploadCredDoc = async (file) => {
    const credId = credFileTarget.current;
    if (!file || !credId) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await apiFetch(`/me/credentials/${credId}/document`, { raw: true, method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      flash("success", "Document attached."); await load();
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); credFileTarget.current = null; }
  };

  const viewCredDoc = async (c) => {
    try {
      const res = await apiFetch(`/users/${user.id}/credentials/${c.id}/document`, { raw: true });
      if (!res.ok) throw new Error(await res.text());
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
  };

  const promote = async () => {
    setBusy(true);
    try {
      await apiFetch("/me/profile/promote", { method: "POST" });
      flash("success", "Publish request submitted for admin approval."); await load();
    } catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try { await apiFetch("/me/profile/promote/withdraw", { method: "POST" }); await load(); }
    catch (e) { flash("error", String(e.message || e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const label = { fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".05em" };
  const successStyle = { background: "#d1fae5", border: "1px solid #6ee7b7", color: "#065f46", padding: "8px 12px", borderRadius: 4, fontSize: 12, marginBottom: 12 };
  const errorStyle = { background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", padding: "8px 12px", borderRadius: 4, fontSize: 12, marginBottom: 12 };

  return (
    <>
      {msg && <div style={msg.type === "success" ? successStyle : errorStyle}>{msg.text}</div>}

      {/* ── Profile card ── */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Employee Profile</h3></div>
        <div className="card-body">
          <div style={{ display: "flex", gap: 22, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ textAlign: "center" }}>
              {headshotUrl
                ? <img src={headshotUrl} alt="Headshot" style={{ width: 110, height: 110, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--border)" }} />
                : <div style={{ width: 110, height: 110, borderRadius: "50%", background: "var(--navy)", color: "#c9a227", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, fontWeight: 600 }}>{initials}</div>}
              <div style={{ marginTop: 8, display: "flex", gap: 6, justifyContent: "center" }}>
                <button className="btn btn-sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>Upload</button>
                {/* WS0-B H22: was a single unconfirmed click that deleted the stored image. */}
                {headshotUrl && <button className="btn btn-sm" onClick={async () => {
                  if (!window.confirm("Remove your headshot?\n\nThe stored image is deleted — you'd need to upload it again.")) return;
                  await apiFetch("/me/profile/headshot", { method: "DELETE" }); setHeadshotUrl(null);
                }} disabled={busy}>Remove</button>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                     onChange={e => { uploadHeadshot(e.target.files[0]); e.target.value = ""; }} />
            </div>
            <div style={{ flex: 1, minWidth: 320 }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Location / Region</label>
                  <input value={region} onChange={e => setRegion(e.target.value)} placeholder="Seattle, WA" />
                </div>
                <div className="form-group">
                  <label>Website Group</label>
                  <select value={group} onChange={e => setGroup(e.target.value)}>
                    <option>Leadership</option><option>Adjusters</option><option>Support</option>
                  </select>
                </div>
                <div className="form-group full">
                  <label>Tile summary <span style={{ fontWeight: 400, color: "var(--text3)" }}>— one sentence shown on your website directory card ({240 - summary.length} left)</span></label>
                  <input maxLength={240} value={summary} onChange={e => setSummary(e.target.value)}
                         placeholder="e.g. 20+ years on Jones Act claims; deep with collective bargaining agreements." />
                </div>
                <div className="form-group full">
                  <label>Bio (blank line between paragraphs)</label>
                  <textarea rows={7} value={bio} onChange={e => setBio(e.target.value)}
                            placeholder="Professional background, experience, focus areas…" />
                </div>
                <div className="form-group full">
                  <label>Specialties</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                    {specIds.map(id => {
                      const s = allSpecs.find(x => x.id === id);
                      return s ? (
                        <span key={id} className="pill-slate" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 12, fontSize: 12, background: "var(--bg3)", border: "1px solid var(--border)" }}>
                          {s.name}
                          {s.status === "proposed" && <span style={{ fontSize: 10, color: "var(--amber-text, #92600a)", fontWeight: 600 }}>pending approval</span>}
                          <span style={{ cursor: "pointer", color: "var(--text3)" }} onClick={() => setSpecIds(specIds.filter(x => x !== id))}>×</span>
                        </span>
                      ) : null;
                    })}
                  </div>
                  <select value="" onChange={e => {
                    if (e.target.value === "__propose__") { setProposeOpen(true); return; }
                    const v = Number(e.target.value); if (v && !specIds.includes(v)) setSpecIds([...specIds, v]);
                  }}>
                    <option value="">+ Add a specialty…</option>
                    {allSpecs.filter(s => !specIds.includes(s.id)).map(s =>
                      <option key={s.id} value={s.id}>{s.name}{s.status === "proposed" ? " (pending approval)" : ""}</option>)}
                    <option value="__propose__">✚ Propose a new specialty…</option>
                  </select>
                  {proposeOpen && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                      <input autoFocus placeholder="New specialty name" value={proposeVal}
                             onChange={e => setProposeVal(e.target.value)}
                             onKeyDown={e => { if (e.key === "Enter") proposeSpecialty(); if (e.key === "Escape") { setProposeOpen(false); setProposeVal(""); } }} />
                      <button className="btn btn-sm btn-primary" onClick={proposeSpecialty} disabled={busy || !proposeVal.trim()}>Propose</button>
                      <button className="btn btn-sm" onClick={() => { setProposeOpen(false); setProposeVal(""); }}>Cancel</button>
                      <span style={{ fontSize: 12, color: "var(--text3)" }}>Needs admin approval before it can appear on the website.</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button className="btn btn-primary" onClick={saveProfile} disabled={busy}>{busy ? "Saving…" : "Save Profile"}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Credentials card ── */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Licenses &amp; Certifications</h3>
          <div style={{ display: "flex", gap: 6 }}>
            {counts.current > 0 && <StatusPill tone="green" sm>{counts.current} current</StatusPill>}
            {counts.expiring > 0 && <StatusPill tone="amber" sm>{counts.expiring} expiring</StatusPill>}
            {counts.expired > 0 && <StatusPill tone="red" sm>{counts.expired} expired</StatusPill>}
            <button className="btn btn-sm btn-primary" onClick={() => setCredForm({ ...EMPTY_CRED })}>+ Add</button>
          </div>
        </div>
        <div className="card-body">
          {creds.length === 0 && !credForm &&
            <EmptyState icon="📜" title="No licenses or certifications yet"
                        hint="Add your adjuster licenses and certifications, attach the document image, and set the expiration date — renewal reminders are automatic." />}
          {creds.length > 0 && (
            <table className="tbl-compact" style={{ width: "100%" }}>
              <thead><tr><th>Type</th><th>Name</th><th>Jurisdiction</th><th>No.</th><th>Issued</th><th>Expires</th><th>Status</th><th>Document</th><th></th></tr></thead>
              <tbody>
                {creds.map(c => (
                  <tr key={c.id}>
                    <td>{KIND_LABEL[c.kind] || c.kind}</td>
                    <td>{c.name}</td>
                    <td>{c.jurisdiction || "—"}</td>
                    <td>{c.credential_no || "—"}</td>
                    <td>{c.issued_on ? fmt.date(c.issued_on) : "—"}</td>
                    <td>{c.expires_on ? fmt.date(c.expires_on) : "—"}</td>
                    <td><StatusPill tone={CRED_TONE[c.status]} sm>{CRED_LABEL[c.status]}</StatusPill></td>
                    <td>
                      {c.has_document
                        ? <button className="btn btn-sm" onClick={() => viewCredDoc(c)}>View</button>
                        : <button className="btn btn-sm" onClick={() => { credFileTarget.current = c.id; credFileRef.current && credFileRef.current.click(); }}>Attach</button>}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-sm" onClick={() => setCredForm({ ...EMPTY_CRED, ...c, issued_on: c.issued_on || "", expires_on: c.expires_on || "", jurisdiction: c.jurisdiction || "", credential_no: c.credential_no || "", notes: c.notes || "" })}>Edit</button>{" "}
                      <button className="btn btn-sm" onClick={() => deleteCred(c)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <input ref={credFileRef} type="file" accept="image/*,.pdf" style={{ display: "none" }}
                 onChange={e => { uploadCredDoc(e.target.files[0]); e.target.value = ""; }} />
          {credForm && (
            <div style={{ marginTop: 14, padding: 14, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6 }}>
              <div style={{ ...label, marginBottom: 8 }}>{credForm.id ? "Edit credential" : "New credential"}</div>
              <div className="form-grid">
                <div className="form-group"><label>Type</label>
                  <select value={credForm.kind} onChange={e => setCredForm({ ...credForm, kind: e.target.value })}>
                    <option value="license">License</option><option value="certification">Certification</option><option value="other">Other</option>
                  </select></div>
                <div className="form-group"><label>Name</label>
                  <input value={credForm.name} onChange={e => setCredForm({ ...credForm, name: e.target.value })} placeholder="Adjuster License" /></div>
                <div className="form-group"><label>Jurisdiction / State</label>
                  <input value={credForm.jurisdiction} onChange={e => setCredForm({ ...credForm, jurisdiction: e.target.value })} placeholder="WA" /></div>
                <div className="form-group"><label>Number</label>
                  <input value={credForm.credential_no} onChange={e => setCredForm({ ...credForm, credential_no: e.target.value })} /></div>
                <div className="form-group"><label>Issued</label>
                  <input type="date" value={credForm.issued_on} onChange={e => setCredForm({ ...credForm, issued_on: e.target.value })} /></div>
                <div className="form-group"><label>Expires</label>
                  <input type="date" value={credForm.expires_on} onChange={e => setCredForm({ ...credForm, expires_on: e.target.value })} /></div>
                <div className="form-group full"><label>Notes</label>
                  <input value={credForm.notes} onChange={e => setCredForm({ ...credForm, notes: e.target.value })} /></div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                <button className="btn btn-sm" onClick={() => setCredForm(null)}>Cancel</button>
                <button className="btn btn-sm btn-primary" onClick={saveCred} disabled={busy || !credForm.name.trim()}>Save</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Website presence card ── */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Website Presence</h3>
          <div>
            {publish.published_at && <StatusPill tone="green" sm>Published {fmt.date(publish.published_at)}</StatusPill>}
            {pending && <StatusPill tone="amber" sm>Awaiting admin approval</StatusPill>}
            {!publish.published_at && !pending && <StatusPill tone="slate" sm>Not on the website</StatusPill>}
          </div>
        </div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 12 }}>
            This is exactly what will appear in the “Our People” section of waltersnorthwest.com once approved.
            Contact info comes from your account (email) and signature settings (direct phone); title is set by an administrator.
          </div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 16, background: "var(--bg)" }}>
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 10 }}>
              {headshotUrl
                ? <img src={headshotUrl} alt="" style={{ width: 54, height: 54, borderRadius: "50%", objectFit: "cover" }} />
                : <div style={{ width: 54, height: 54, borderRadius: "50%", background: "var(--navy)", color: "#c9a227", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>{initials}</div>}
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{preview.name}</div>
                <div style={{ fontSize: 13, color: "var(--text2)" }}>{preview.title || <em>no title set — ask an admin</em>} · {preview.group}</div>
                <div style={{ fontSize: 12, color: "var(--text3)" }}>{preview.region || "no location set"} · {preview.phone || "no phone"} · {preview.email}</div>
              </div>
            </div>
            {preview.licenses && <div style={{ fontSize: 12.5, marginBottom: 8 }}><b>Licensed:</b> {preview.licenses}</div>}
            {(preview.specs || []).length > 0 &&
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                {preview.specs.map(s => <span key={s} style={{ fontSize: 11.5, padding: "2px 9px", borderRadius: 10, background: "var(--bg3)", border: "1px solid var(--border)" }}>{s}</span>)}
              </div>}
            {(preview.bio || []).map((p, i) => <p key={i} style={{ fontSize: 13, color: "var(--text2)", margin: "0 0 8px" }}>{p}</p>)}
            {(preview.bio || []).length === 0 && <div style={{ fontSize: 12.5, color: "var(--red, #ef4444)" }}>Add a bio above before promoting — the website entry needs one.</div>}
          </div>
          {publish.last_request && publish.last_request.status === "rejected" &&
            <div style={{ marginTop: 10, fontSize: 12.5, color: "#991b1b" }}>
              Last request was declined{publish.last_request.decision_note ? `: ${publish.last_request.decision_note}` : ""} — update your profile and promote again.
            </div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            {pending
              ? <button className="btn" onClick={withdraw} disabled={busy}>Withdraw request</button>
              : <button className="btn btn-primary" onClick={promote} disabled={busy || (preview.bio || []).length === 0}>
                  {publish.published_at ? "Promote updated profile" : "Promote to Website"}
                </button>}
          </div>
        </div>
      </div>
    </>
  );
}
